import { Checkbox, IconButton } from '@fluentui/react';
import { FormControl } from '@v2/components/controls/FormControl/FormControl';
import { ValidationError } from '@v2/components/controls/ValidationError/ValidationError';
import HSObjectPicker from '@v2/components/controls/implementations/HSObjectPicker';
import { PropertyEdit } from '@v2/components/flowsteps/editors/hubspot/objects/PullOjbectHS/PropertyEdit/PropertyEdit';
import { AdvancedSettings } from '@v2/components/flowsteps/editors/common/AdvancedSettings/AdvancedSettings';
import { FlowstepButtons } from '@v2/components/flowsteps/editors/common/FlowstepButtons/FlowstepButtons';
import { FlowstepContainer } from '@v2/components/flowsteps/editors/common/FlowstepContainer/FlowstepContainer';
import TitledPage from '@v2/components/layout/TitledPage';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FlowstepType, RangeType } from 'shared/utils/flowstep';
import RangeSelector from 'src/components/Flowsteps/PushData/RangeSelector';
import GroupPane from 'src/components/base/GroupPane';
import SearchableComboBox from 'src/components/base/SearchableComboBox';
import { runShortAction } from 'src/services/utils';
import { useStores } from 'src/v2/hooks/useStores';
import { useStyles } from './DeleteObjectsHS.styles';
import { DeleteObjectsHSState } from './delete-objects-hs-state';
import { appState } from "@v2/app-state";
import {
    ExecutionConfirmationDialog
} from "@v2/components/dialogs/ExecutionConfirmationDialog/ExecutionConfirmationDialog";
import UnsavedChangesDialog from "@v2/hooks/UnsavedChangesDialog";

interface DeleteObjectsHSProps {
    id?: string;
    createNew?: boolean;
    rangeToApply?: typeof RangeType[keyof typeof RangeType];
}

export const DeleteObjectsHS: React.FC<DeleteObjectsHSProps> = observer(({ id, createNew, rangeToApply }) => {
    const styles = useStyles();
    const { officeStore, requestStore } = useStores();

    const [state] = useState(() => new DeleteObjectsHSState(rangeToApply));

    const [optionsCollapsed, setOptionsCollapsed] = useState<boolean>(true);
    const [advancedCollapsed, setAdvancedCollapsed] = useState<boolean>(true);

    useEffect(() => {
        (async () => {
            await state.loadFlowstep(id, createNew);
        })();
    }, [id, createNew]);

    useEffect(() => {
        return () => {
            state.dispose();
        }
    }, [])

    useEffect(() => {
        const error = state.loadingObjectFieldsError || state.loadingObjectTypesError;
        if (error) {
            requestStore.raise(error, true)
        }
    }, [state.loadingObjectFieldsError, state.loadingObjectTypesError]);

    let sectionNumber = 1;

    const title = `${state.isNewFlowstep ? 'New: ' : ''}Delete ${state.selectedObjectType?.name || 'Objects'}`;

    const handleDetectClick = async () => {
        await runShortAction(async () => {
            await state.detectRange();
            if (!state.flowstep?.id) {
                await state.findAndLoadSimilarFlowstep(false);
            }
        });
    }

    const handleRangeChange = (value: any, isValid: boolean) => {
        state.rangeToApply = value;
        state.isValidRangeToApply = isValid;
    }

    const idColumnOptions = state.tableHeaders.map((header: string) => ({
        key: header,
        text: header
    }));

    const getTimestampSheetId = () => {
        return state.flowstep?.options.worksheetId ?? officeStore.activeWorksheetId;
    }

    const handleRunClick = async () => {
        const flowstep = state.buildFlowstep();

        const isConfirmed = await appState.showDialog((setResult) =>
          <ExecutionConfirmationDialog
            onResult={(value) => setResult(value)}
            flowstep={flowstep}
          />
        );

        if (isConfirmed) {
            await state.runFlowstep();
        }
    }

    return <>
        <UnsavedChangesDialog
            onSave={state.saveFlowstep}
            isNotSaved={() => state.isNotSaved}
        />
        <TitledPage title={title}>
            <FlowstepContainer>
                <FormControl label={`${sectionNumber++}. Verify range`}>
                    <div className={styles.verifyRange}>
                        <IconButton
                            iconProps={{ iconName: 'xappex-target' }}
                            styles={{ icon: { fontSize: '24px' } }}
                            onClick={() => handleDetectClick()}
                        />
                        <span>{state.isDetectingRange
                            ? 'detecting range...'
                            : state.targetRangeAddress?.addressWithSheet || 'not detected'
                        }</span>
                    </div>
                    <ValidationError
                        className={styles.validationError}
                        message={!state.isDetectingRange && state.validationErrors.get('target-range-not-selected')}
                    />
                </FormControl>

                <FormControl label={`${sectionNumber++}. Select the object to delete data from`}>
                    <HSObjectPicker
                        data={state.objectTypes.data}
                        value={state.selectedObjectType}
                        onChange={(_, newValue) => state.objectTypeName = newValue?.name}
                        disabled={state.objectTypes.isLoading}
                        errorMessage={state.validationErrors.get('object-type-not-selected')}
                    />
                </FormControl>

                <FormControl label={`${sectionNumber++}. Select ID column`}>
                    <SearchableComboBox
                        options={idColumnOptions}
                        selectedKey={state.idColumn}
                        onChange={(_, option) => state.idColumn = option?.key || ''}
                        placeholder="Select the column containing object IDs"
                        disabled={state.tableHeaders.length === 0}
                        errorMessage={state.validationErrors.get('id-column-not-selected')}
                    />
                </FormControl>

                <FormControl label={`${sectionNumber++}. Select rows to delete`}>
                    <RangeSelector
                        rangeToApply={state.rangeToApply}
                        flowstepType={FlowstepType.Hubspot}
                        headers={state.tableHeaders}
                        disabled={false}
                        onChange={(value: any, isValid: boolean) => handleRangeChange(value, isValid)}
                        hideIdColumnName={true}
                        dontResetOnHeaderChange={true}
                    />
                </FormControl>

                <GroupPane
                    title="Options"
                    collapsed={optionsCollapsed}
                    onCollapsed={(collapsed: boolean) => setOptionsCollapsed(collapsed)}
                    tooltip=""
                    className={styles.groupPane}
                >
                    <div className={styles.options}>
                        <PropertyEdit
                            label="Show operations results"
                            tooltip={`Enable, if you would like to see a table alongside your table 
              with data, where results of executed delete operations are displayed`}
                        >
                            <Checkbox
                                checked={state.showResultsTable}
                                onChange={(_, checked) => state.showResultsTable = !!checked}
                            />
                        </PropertyEdit>
                        <PropertyEdit
                            label="Don't delete hidden rows"
                            tooltip={`Enable, if you would like delete only visible rows. 
              Rows hidden by filters or manually, will not be deleted.`}
                        >
                            <Checkbox
                                checked={state.onlyVisibleRows}
                                onChange={(_, checked) => state.onlyVisibleRows = !!checked}
                            />
                        </PropertyEdit>
                    </div>
                </GroupPane>

                <GroupPane
                    title="Advanced"
                    collapsed={advancedCollapsed}
                    onCollapsed={(collapsed: boolean) => setAdvancedCollapsed(collapsed)}
                    tooltip=""
                    className={styles.groupPane}
                >
                    <AdvancedSettings
                        baseEditorState={state}
                        namePlaceholder={state.generatedFlowstepName}
                        timestampCellProps={{ getTimestampSheetId }}
                    />
                </GroupPane>

                <FlowstepButtons
                    editorState={state}
                    disabled={state.isFlowstepButtonsDisabled}
                    onRunClick={() => handleRunClick()}
                />
            </FlowstepContainer>
        </TitledPage>
    </>;
});
